import { Warranty } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { TextFieldProps } from '@mui/material';
import { t } from '@translations';

type Field<T> = {
  label: string;
  name: T;
  type?: 'date' | 'number' | 'text' | 'file' | 'brand';
  minDate?: string;
} & Omit<TextFieldProps, 'name'>;

type DealerFieldNames = keyof Pick<Warranty, 'dealerName' | 'dealerContact' | 'dealerEmail'>;

type CustomerFields = keyof Pick<
  Warranty,
  'customerName' | 'customerAddress' | 'customerPostCode' | 'customerCity'
>;

type ProductFields = keyof Pick<
  Warranty,
  | 'serialNo'
  | 'brand'
  | 'productType'
  | 'engineNo'
  | 'recipeFile'
  | 'hoursOfWork'
  | 'saleDate'
  | 'destinationCode'
  | 'damageDate'
  | 'repairDate'
>;

export const dealerFields = (): Field<DealerFieldNames>[] => [
  {
    name: 'dealerName',
    label: t(TextCodes.Warranty.Block.DealerName),
    required: true,
  },
  {
    name: 'dealerContact',
    label: t(TextCodes.Warranty.Block.ContactPerson),
    required: true,
  },
  {
    name: 'dealerEmail',
    label: t(TextCodes.Warranty.Block.Email),
    required: true,
  },
];

export const customerFields = (): Field<CustomerFields>[] => [
  {
    name: 'customerName',
    label: t(TextCodes.Warranty.Block.CustomerName),
    required: true,
  },
  {
    name: 'customerAddress',
    label: t(TextCodes.Warranty.Block.Address),
    required: false,
  },
  {
    name: 'customerPostCode',
    label: t(TextCodes.Warranty.Block.PostCode),
    required: false,
  },
  {
    name: 'customerCity',
    label: t(TextCodes.Warranty.Block.City),
    required: false,
  },
];

type ExtendedFieldItem = Field<ProductFields> | Field<ProductFields>[];

type ExtendedField = ExtendedFieldItem[];

export const productFieldsLeft = (): ExtendedField => [
  [
    {
      name: 'serialNo',
      label: t(TextCodes.Warranty.Block.SerialNo),
      required: true,
    },
    {
      name: 'brand',
      label: t(TextCodes.Warranty.Block.Brand),
      type: 'brand',
    },
  ],
  [
    {
      name: 'productType',
      label: t(TextCodes.Warranty.Block.ProductType),
      required: true,
    },
  ],
  [
    {
      name: 'engineNo',
      label: t(TextCodes.Warranty.Block.EngineNo),
    },
    {
      name: 'recipeFile',
      label: t(TextCodes.Warranty.Block.UploadRecipe),
      type: 'file',
    },
  ],
];

export const productFieldsRight = (): ExtendedField => [
  [
    {
      name: 'hoursOfWork',
      label: t(TextCodes.Warranty.Block.HoursOfWork),
      type: 'number',
      required: true,
    },
  ],
  [
    {
      name: 'saleDate',
      label: t(TextCodes.Warranty.Block.SaleDate),
      type: 'date',
      required: true,
    },
    {
      name: 'destinationCode',
      label: t(TextCodes.Warranty.Block.DestinationCode),
    },
  ],
  [
    {
      name: 'damageDate',
      label: t(TextCodes.Warranty.Block.DamageDate),
      type: 'date',
      required: true,
    },
    {
      name: 'repairDate',
      label: t(TextCodes.Warranty.Block.RepairDate),
      type: 'date',
      required: true,
    },
  ],
];

export const productFields = () => [productFieldsLeft(), productFieldsRight()];
